export const APP_NAME = 'KasaGO'
export const APP_TAGLINE = 'Find your next stay.'

// Mirrors the user_role DB enum (schema.sql 0001)
export type UserRole = 'guest' | 'owner' | 'admin'

// Mirrors property_status DB enum
export type PropertyStatus =
  | 'draft'
  | 'pending_review'
  | 'live'
  | 'suspended'
  | 'rejected'

// Mirrors booking_status DB enum
export type BookingStatus =
  | 'pending_payment'
  | 'confirmed'
  | 'cancelled'
  | 'completed'
  | 'no_show'

// Mirrors payment_status DB enum
export type PaymentStatus =
  | 'pending'
  | 'paid'
  | 'failed'
  | 'refunded'
  | 'partially_refunded'

// Mirrors policy_type DB enum
export type PolicyType = 'free_cancellation' | 'non_refundable'

// Mirrors penalty_type DB enum
export type PenaltyType = 'first_night' | 'full_amount'

// Platform defaults — real values come from platform_settings table (Iteration 1)
export const DEFAULT_COMMISSION_RATE = 10       // %
export const DEFAULT_COMMISSION_VAT_RATE = 12   // %
// Guest VAT charged on the listing price (migration 0013). Guest pays
// subtotal + GUEST_VAT_RATE%. Single source for all guest-facing VAT display;
// authoritative amount is still computed server-side in create_booking_hold.
export const GUEST_VAT_RATE = 12                // %
export const DEFAULT_HOLD_MINUTES = 30
export const DEFAULT_PAYOUT_DELAY_DAYS = 2

// localStorage key for the extranet's active property (owners can have several)
export const EXTRANET_PROPERTY_KEY = 'kasa_extranet_property'

// Feature flags
// Channex channel-manager integration is fully built but not yet certified with
// Channex, so the extranet "Channel Mgr" entry is hidden for launch. Set
// VITE_CHANNEX_ENABLED=true (Cloudflare Pages env) to re-enable, then redeploy —
// VITE_* vars are inlined at build time, so setting it alone changes nothing.
export const CHANNEX_ENABLED = import.meta.env.VITE_CHANNEX_ENABLED === 'true'

// TanStack Query stale times (ms) — CLAUDE.md §6
export const STALE = {
  catalog: 5 * 60 * 1000,       // 5 min
  availability: 30 * 1000,      // 30 s
  bookings: 0,                   // always fresh
} as const

// Booking reference prefix
export const REFERENCE_PREFIX = 'KSG'

// Display currency
export const CURRENCY = 'PHP'
export const CURRENCY_SYMBOL = '₱'

// Asia/Manila timezone used for all display logic
export const MANILA_TZ = 'Asia/Manila'

// ── Amenity system ────────────────────────────────────────────────────────────

export type AmenityCategory =
  | 'connectivity'
  | 'food_drink'
  | 'pool_beach'
  | 'wellness'
  | 'services'
  | 'transportation'
  | 'family'
  | 'accessibility'
  | 'safety'
  | 'room_features'
  | 'business'

export interface AmenityDef {
  label:    string
  category: AmenityCategory
  popular?: true
}

export const AMENITY_CATEGORY_LABELS: Record<AmenityCategory, string> = {
  connectivity:   'Connectivity',
  food_drink:     'Food & Drink',
  pool_beach:     'Pool & Beach',
  wellness:       'Wellness & Fitness',
  services:       'Services',
  transportation: 'Transportation',
  family:         'Family & Kids',
  accessibility:  'Accessibility',
  safety:         'Safety & Security',
  room_features:  'Room Features',
  business:       'Business',
}

export const AMENITY_CATEGORY_ORDER: AmenityCategory[] = [
  'connectivity', 'food_drink', 'pool_beach', 'wellness',
  'services', 'transportation', 'family', 'accessibility',
  'safety', 'room_features', 'business',
]

export const AMENITY_DEFINITIONS: Record<string, AmenityDef> = {
  // Connectivity
  wifi:                { label: 'Free WiFi',              category: 'connectivity',   popular: true },
  paid_wifi:           { label: 'Paid WiFi',              category: 'connectivity' },

  // Food & Drink
  restaurant:          { label: 'Restaurant',             category: 'food_drink',     popular: true },
  bar:                 { label: 'Bar',                    category: 'food_drink' },
  breakfast_included:  { label: 'Breakfast included',     category: 'food_drink',     popular: true },
  breakfast:           { label: 'Breakfast',              category: 'food_drink',     popular: true },
  room_service:        { label: 'Room service',           category: 'food_drink' },
  minibar:             { label: 'Minibar',                category: 'food_drink' },
  poolside_bar:        { label: 'Poolside bar',           category: 'food_drink' },

  // Pool & Beach
  pool:                { label: 'Swimming pool',          category: 'pool_beach',     popular: true },
  indoor_pool:         { label: 'Indoor pool',            category: 'pool_beach' },
  infinity_pool:       { label: 'Infinity pool',          category: 'pool_beach' },
  kids_pool:           { label: "Children's pool",        category: 'pool_beach' },
  beach_access:        { label: 'Beach access',           category: 'pool_beach',     popular: true },
  ocean_view:          { label: 'Ocean view',             category: 'pool_beach' },
  mountain_view:       { label: 'Mountain view',          category: 'pool_beach' },
  water_sports:        { label: 'Water sports',           category: 'pool_beach' },

  // Wellness
  gym:                 { label: 'Fitness center',         category: 'wellness',       popular: true },
  spa:                 { label: 'Spa',                    category: 'wellness' },
  sauna:               { label: 'Sauna',                  category: 'wellness' },
  hot_tub:             { label: 'Hot tub',                category: 'wellness' },

  // Services
  airport_shuttle:     { label: 'Airport shuttle',        category: 'services',       popular: true },
  laundry:             { label: 'Laundry service',        category: 'services' },
  dry_cleaning:        { label: 'Dry cleaning',           category: 'services' },
  concierge:           { label: 'Concierge',              category: 'services' },
  front_desk_24h:      { label: '24-hour front desk',     category: 'services' },
  luggage_storage:     { label: 'Luggage storage',        category: 'services' },
  express_checkin:     { label: 'Express check-in/out',   category: 'services' },

  // Transportation
  parking:             { label: 'Free parking',           category: 'transportation', popular: true },
  paid_parking:        { label: 'Paid parking',           category: 'transportation' },
  car_rental:          { label: 'Car rental',             category: 'transportation' },
  taxi_service:        { label: 'Taxi service',           category: 'transportation' },

  // Family
  family_rooms:        { label: 'Family rooms',           category: 'family' },
  playground:          { label: 'Playground',             category: 'family' },
  babysitting:         { label: 'Babysitting',            category: 'family' },
  kids_club:           { label: "Kids' club",             category: 'family' },

  // Accessibility
  elevator:            { label: 'Elevator',               category: 'accessibility' },
  wheelchair_access:   { label: 'Wheelchair accessible',  category: 'accessibility' },
  disabled_facilities: { label: 'Facilities for disabled',category: 'accessibility' },

  // Safety
  cctv:                { label: 'CCTV surveillance',      category: 'safety' },
  security_24h:        { label: '24-hour security',       category: 'safety' },
  fire_extinguisher:   { label: 'Fire extinguisher',      category: 'safety' },
  smoke_alarms:        { label: 'Smoke alarms',           category: 'safety' },
  safe_deposit:        { label: 'In-room safe',           category: 'safety' },
  non_smoking:         { label: 'Non-smoking property',   category: 'safety' },

  // Room Features
  air_conditioning:    { label: 'Air conditioning',       category: 'room_features',  popular: true },
  heating:             { label: 'Heating',                category: 'room_features' },
  balcony:             { label: 'Balcony',                category: 'room_features' },
  pet_friendly:        { label: 'Pet-friendly',           category: 'room_features' },
  non_smoking_rooms:   { label: 'Non-smoking rooms',      category: 'room_features' },
  connecting_rooms:    { label: 'Connecting rooms',       category: 'room_features' },

  // Business
  meeting_rooms:       { label: 'Meeting rooms',          category: 'business' },
  business_center:     { label: 'Business center',        category: 'business' },
  coworking_space:     { label: 'Co-working space',       category: 'business' },
}

// ── Room amenity system ───────────────────────────────────────────────────────

export type RoomAmenityCategory =
  | 'view'
  | 'bathroom'
  | 'climate'
  | 'entertainment'
  | 'connectivity'
  | 'comfort'
  | 'kitchen'

export interface RoomAmenityDef {
  label:    string
  category: RoomAmenityCategory
}

export const ROOM_AMENITY_CATEGORY_LABELS: Record<RoomAmenityCategory, string> = {
  view:          'Views',
  bathroom:      'Bathroom',
  climate:       'Climate',
  entertainment: 'Entertainment',
  connectivity:  'Connectivity',
  comfort:       'Comfort & Furnishings',
  kitchen:       'Kitchen',
}

export const ROOM_AMENITY_CATEGORY_ORDER: RoomAmenityCategory[] = [
  'view', 'bathroom', 'climate', 'entertainment', 'connectivity', 'comfort', 'kitchen',
]

export const ROOM_AMENITY_DEFINITIONS: Record<string, RoomAmenityDef> = {
  // Views
  ocean_view:        { label: 'Ocean / sea view',       category: 'view' },
  mountain_view:     { label: 'Mountain view',           category: 'view' },
  city_view:         { label: 'City view',               category: 'view' },
  garden_view:       { label: 'Garden view',             category: 'view' },
  pool_view:         { label: 'Pool view',               category: 'view' },
  river_view:        { label: 'River view',              category: 'view' },
  // Bathroom
  private_bathroom:  { label: 'Private bathroom',        category: 'bathroom' },
  bathtub:           { label: 'Bathtub',                 category: 'bathroom' },
  shower:            { label: 'Walk-in shower',          category: 'bathroom' },
  hot_water:         { label: 'Hot water',               category: 'bathroom' },
  hairdryer:         { label: 'Hairdryer',               category: 'bathroom' },
  toiletries:        { label: 'Free toiletries',         category: 'bathroom' },
  bathrobe:          { label: 'Bathrobe & slippers',     category: 'bathroom' },
  // Climate
  room_ac:           { label: 'Air conditioning',        category: 'climate' },
  room_heating:      { label: 'Heating',                 category: 'climate' },
  fan:               { label: 'Fan',                     category: 'climate' },
  // Entertainment
  tv:                { label: 'TV',                      category: 'entertainment' },
  cable_tv:          { label: 'Cable / satellite TV',    category: 'entertainment' },
  smart_tv:          { label: 'Smart TV',                category: 'entertainment' },
  streaming:         { label: 'Streaming services',      category: 'entertainment' },
  // Connectivity
  room_wifi:         { label: 'Free WiFi',               category: 'connectivity' },
  work_desk:         { label: 'Work desk & chair',       category: 'connectivity' },
  // Comfort
  balcony:           { label: 'Balcony / terrace',       category: 'comfort' },
  wardrobe:          { label: 'Wardrobe / closet',       category: 'comfort' },
  room_safe:         { label: 'In-room safe',            category: 'comfort' },
  iron:              { label: 'Iron & ironing board',    category: 'comfort' },
  sofa:              { label: 'Sofa',                    category: 'comfort' },
  blackout_curtains: { label: 'Blackout curtains',       category: 'comfort' },
  // Kitchen
  kitchenette:       { label: 'Kitchenette',             category: 'kitchen' },
  microwave:         { label: 'Microwave',               category: 'kitchen' },
  refrigerator:      { label: 'Refrigerator',            category: 'kitchen' },
  coffee_maker:      { label: 'Coffee maker',            category: 'kitchen' },
  electric_kettle:   { label: 'Electric kettle',         category: 'kitchen' },
}

// Search defaults
export const SEARCH_DEFAULTS = {
  adults: 2,
  children: 0,
  rooms: 1,
  limit: 20,
  maxLimit: 50,
} as const
